import React, { useState, useRef } from 'react';
import { getSocket, isSocketConnected } from '../socket';
import { uploadFile, compressImageForUpload } from '../firebase';
import FitText from './FitText';

// Kanban board for 3D model work items.
// Status pipeline: needed → briefed → inprogress → delivered → wired

export const ASSET_STATUSES = [
  { key: 'needed', label: 'Needed', icon: 'pending', color: '#9A9FA5' },
  { key: 'inprogress', label: 'In Progress', icon: 'draw', color: '#3BA6FF' },
  { key: 'delivered', label: 'Completed', icon: 'inventory_2', color: '#BA68C8' },
  { key: 'wired', label: 'In Game', icon: 'check_circle', color: '#4CAF50' }
];

export const ASSET_CATEGORIES = [
  { key: 'hard-surface', label: 'Hard Surface' },
  { key: 'prop', label: 'Prop' },
  { key: 'creature', label: 'Creature' },
  { key: 'foliage', label: 'Foliage / Organic' },
  { key: 'ship', label: 'Ship' },
  { key: 'environment', label: 'Environment' },
  { key: 'other', label: 'Other' }
];

const statusMeta = (key) => ASSET_STATUSES.find(s => s.key === key) || ASSET_STATUSES[0];
const categoryLabel = (key) => (ASSET_CATEGORIES.find(c => c.key === key) || { label: key || 'Other' }).label;

function AssetEditor({ project, asset, memberOptions, onClose }) {
  const isNew = !asset;
  const [name, setName] = useState(asset?.name || '');
  const [category, setCategory] = useState(asset?.category || 'other');
  const [status, setStatus] = useState(asset?.status || 'needed');
  const [assigneeId, setAssigneeId] = useState(asset?.assigneeId || '');
  const [description, setDescription] = useState(asset?.description || '');
  const [notes, setNotes] = useState(asset?.notes || '');
  const [tempImageUrl, setTempImageUrl] = useState(asset?.tempImageUrl || null);
  const [aiPlaceholder, setAiPlaceholder] = useState(!!asset?.aiPlaceholder);
  const [urgent, setUrgent] = useState(!!asset?.urgent);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const imageInputRef = useRef(null);

  const uploadAssetImage = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploadingImage(true);
    try {
      const compressed = await compressImageForUpload(file, { maxDimension: 1280, quality: 0.8 });
      const path = `project-files/${project.projectId}/assets/${Date.now()}_${file.name || 'pasted.png'}`;
      const url = await uploadFile(compressed || file, path, { contentType: (compressed || file).type });
      setTempImageUrl(url);
    } catch (err) {
      console.error('Asset image upload failed:', err);
    }
    setUploadingImage(false);
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    await uploadAssetImage(file);
  };

  // Ctrl+V an image while the editor is open sets it as the card image
  React.useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            uploadAssetImage(file);
          }
          return;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    if (!name.trim()) return;
    const socket = getSocket();
    if (!socket || !isSocketConnected()) return;
    const assigneeName = memberOptions.find(m => m.id === assigneeId)?.name || null;
    if (isNew) {
      socket.emit('project:asset:create', {
        projectId: project.projectId,
        name: name.trim(),
        category,
        status,
        assigneeId: assigneeId || null,
        assigneeName,
        description: description.trim(),
        notes: notes.trim(),
        tempImageUrl,
        aiPlaceholder,
        urgent
      });
    } else {
      socket.emit('project:asset:update', {
        projectId: project.projectId,
        assetId: asset.assetId,
        updates: {
          name: name.trim(),
          category,
          status,
          assigneeId: assigneeId || null,
          assigneeName,
          description: description.trim(),
          notes: notes.trim(),
          tempImageUrl,
          aiPlaceholder,
          urgent
        }
      });
    }
    onClose();
  };

  const handleDelete = () => {
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:asset:delete', { projectId: project.projectId, assetId: asset.assetId });
    }
    onClose();
  };

  return (
    <div className="pv-asset-editor-overlay" onClick={onClose}>
      <div className="pv-asset-editor" onClick={(e) => e.stopPropagation()}>
        <div className="pv-asset-editor-header">
          <h3>{isNew ? 'New Asset' : 'Edit Asset'}</h3>
          <button className="pv-asset-editor-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="pv-asset-editor-body">
          <div className="pv-ae-field">
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Air Canister"
              autoFocus={isNew}
            />
          </div>

          <div className="pv-ae-row">
            <div className="pv-ae-field">
              <label>Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {ASSET_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="pv-ae-field">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {ASSET_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="pv-ae-field">
              <label>Assigned to</label>
              <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                <option value="">Unassigned</option>
                {memberOptions.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          </div>

          <div className="pv-ae-field">
            <label>What it is / what's needed</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this object is in the game and what the real model needs to be..."
              rows={3}
            />
          </div>

          <div className="pv-ae-field pv-ae-checkbox-field">
            <label htmlFor="ae-ai-placeholder">Current in-game model is an AI placeholder</label>
            <input
              id="ae-ai-placeholder"
              type="checkbox"
              checked={aiPlaceholder}
              onChange={(e) => setAiPlaceholder(e.target.checked)}
            />
          </div>

          <div className="pv-ae-field pv-ae-checkbox-field urgent">
            <label htmlFor="ae-urgent">Urgent</label>
            <input
              id="ae-urgent"
              type="checkbox"
              checked={urgent}
              onChange={(e) => setUrgent(e.target.checked)}
            />
          </div>

          <div className="pv-ae-field">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Scale, format, anything else..."
              rows={2}
            />
          </div>

          <div className="pv-ae-field">
            <label>Current in-game look (temp/placeholder)</label>
            {tempImageUrl ? (
              <div className="pv-ae-image-preview">
                <img src={tempImageUrl} alt="Current in-game temp" />
                <button className="pv-ae-image-remove" onClick={() => setTempImageUrl(null)} title="Remove image">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            ) : (
              <button
                className="pv-ae-image-add"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadingImage}
              >
                <span className="material-symbols-outlined">add_photo_alternate</span>
                {uploadingImage ? 'Uploading...' : 'Add screenshot'}
              </button>
            )}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImagePick}
            />
          </div>
        </div>

        <div className="pv-asset-editor-footer">
          {!isNew && (
            confirmDelete ? (
              <div className="pv-ae-delete-confirm">
                <span>Delete?</span>
                <button className="pv-ae-delete-yes" onClick={handleDelete}>Yes</button>
                <button className="pv-ae-delete-no" onClick={() => setConfirmDelete(false)}>No</button>
              </div>
            ) : (
              <button className="pv-ae-delete-btn" onClick={() => setConfirmDelete(true)}>
                <span className="material-symbols-outlined">delete</span>
                Delete
              </button>
            )
          )}
          <div className="pv-ae-footer-spacer" />
          <button className="pv-ae-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="pv-ae-save-btn" onClick={handleSave} disabled={!name.trim() || uploadingImage}>
            {isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectAssetBoard({ user, project, assets, friends }) {
  const [editing, setEditing] = useState(null); // asset object or 'new'
  const [dragAssetId, setDragAssetId] = useState(null);
  const [dragOverStatus, setDragOverStatus] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');

  // Member options for the assignee picker
  const memberOptions = (project.members || []).map(id => {
    if (id === user.uid) return { id, name: user.displayName || 'You' };
    const friend = friends.find(f => f.userId === id);
    return { id, name: friend?.username || 'Unknown' };
  });

  const filtered = filterCategory ? assets.filter(a => a.category === filterCategory) : assets;
  const [dropHint, setDropHint] = useState(null); // { status, beforeAssetId|null }

  const columnAssets = (statusKey) =>
    filtered.filter(a => a.status === statusKey).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  // Drop at a position: reorder within a column or move + position across columns
  const handleDropAt = (statusKey, beforeAssetId) => {
    setDragOverStatus(null);
    setDropHint(null);
    if (!dragAssetId) return;
    const asset = assets.find(a => a.assetId === dragAssetId);
    setDragAssetId(null);
    if (!asset) return;
    const list = assets.filter(a => a.status === statusKey && a.assetId !== asset.assetId)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    let idx = beforeAssetId ? list.findIndex(a => a.assetId === beforeAssetId) : list.length;
    if (idx < 0) idx = list.length;
    let sortOrder;
    if (list.length === 0) sortOrder = 1000;
    else if (idx === 0) sortOrder = (list[0].sortOrder || 0) - 1000;
    else if (idx === list.length) sortOrder = (list[list.length - 1].sortOrder || 0) + 1000;
    else sortOrder = ((list[idx - 1].sortOrder || 0) + (list[idx].sortOrder || 0)) / 2;
    if (asset.status === statusKey && Math.abs((asset.sortOrder || 0) - sortOrder) < 1e-9) return;
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:asset:update', {
        projectId: project.projectId,
        assetId: asset.assetId,
        updates: { status: statusKey, sortOrder }
      });
    }
  };

  const toggleUrgent = (asset, e) => {
    e.stopPropagation();
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:asset:update', {
        projectId: project.projectId,
        assetId: asset.assetId,
        updates: { urgent: !asset.urgent }
      });
    }
  };

  const usedCategories = [...new Set(assets.map(a => a.category))];

  return (
    <div className="pv-asset-board">
      <div className="pv-asset-board-header">
        <span className="pv-asset-board-count">
          {assets.length} asset{assets.length !== 1 ? 's' : ''}
        </span>
        {usedCategories.length > 1 && (
          <select
            className="pv-asset-filter"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All categories</option>
            {ASSET_CATEGORIES.filter(c => usedCategories.includes(c.key)).map(c => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        )}
        <button className="pv-asset-add-btn" onClick={() => setEditing('new')}>
          <span className="material-symbols-outlined">add</span>
          New Asset
        </button>
      </div>

      {assets.length === 0 ? (
        <div className="pv-empty-files">
          <span className="material-symbols-outlined">view_in_ar</span>
          <p>No assets on the board yet</p>
        </div>
      ) : (
        <div className="pv-asset-columns">
          {ASSET_STATUSES.map(col => {
            const colAssets = columnAssets(col.key);
            return (
              <div
                key={col.key}
                className={`pv-asset-column ${dragOverStatus === col.key ? 'drag-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverStatus(col.key); if (dragAssetId) setDropHint({ status: col.key, beforeAssetId: null }); }}
                onDragLeave={() => setDragOverStatus(prev => prev === col.key ? null : prev)}
                onDrop={(e) => { e.preventDefault(); handleDropAt(col.key, null); }}
              >
                <div className="pv-asset-column-header" style={{ color: col.color }}>
                  <span className="material-symbols-outlined">{col.icon}</span>
                  <span className="pv-asset-column-title">{col.label}</span>
                  <span className="pv-asset-column-count">{colAssets.length}</span>
                </div>
                <div className="pv-asset-column-cards">
                  {colAssets.map(asset => (
                    <div
                      key={asset.assetId}
                      className={`pv-asset-card ${asset.urgent ? 'urgent' : ''} ${dropHint?.status === col.key && dropHint?.beforeAssetId === asset.assetId ? 'drop-before' : ''} ${dragAssetId === asset.assetId ? 'dragging' : ''}`}
                      draggable
                      onDragStart={() => setDragAssetId(asset.assetId)}
                      onDragEnd={() => { setDragAssetId(null); setDragOverStatus(null); setDropHint(null); }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (dragAssetId && dragAssetId !== asset.assetId) setDropHint({ status: col.key, beforeAssetId: asset.assetId });
                      }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDropAt(col.key, asset.assetId); }}
                      onClick={() => setEditing(asset)}
                    >
                      <button
                        className={`pv-asset-urgent-btn ${asset.urgent ? 'on' : ''}`}
                        onClick={(e) => toggleUrgent(asset, e)}
                        title={asset.urgent ? 'Clear urgent' : 'Mark urgent'}
                      >
                        <span className="material-symbols-outlined">priority_high</span>
                      </button>
                      {asset.tempImageUrl && (
                        <div className="pv-asset-card-thumb">
                          <img src={asset.tempImageUrl} alt={asset.name} loading="lazy" decoding="async" />
                        </div>
                      )}
                      <div className="pv-asset-card-body">
                        <FitText className="pv-asset-card-name" text={asset.name} max={13} min={10} />
                        {asset.description && (
                          <span className="pv-asset-card-desc">{asset.description}</span>
                        )}
                        <div className="pv-asset-card-meta">
                          <span className="pv-asset-card-cat">{categoryLabel(asset.category)}</span>
                          {asset.aiPlaceholder && (
                            <span className="pv-asset-card-ai" title="The current in-game model is an AI placeholder">AI TEMP</span>
                          )}
                          {asset.assigneeName ? (
                            <span className="pv-asset-card-assignee">
                              <span className="material-symbols-outlined">person</span>
                              {asset.assigneeId === user.uid ? 'You' : asset.assigneeName}
                            </span>
                          ) : (
                            <span className="pv-asset-card-assignee unassigned">Unassigned</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <AssetEditor
          project={project}
          asset={editing === 'new' ? null : editing}
          memberOptions={memberOptions}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

export { statusMeta };
export default ProjectAssetBoard;
